import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTeamCalendar } from '@/hooks/useTeamCalendar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  StickyNote,
  Plus,
  Edit2,
  Trash2,
  Users,
  Sun,
  CalendarDays,
  Thermometer
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Consistent animation config
const EASE = [0.25, 0.46, 0.45, 0.94];

// StatPill component matching Dashboard pattern
const StatPill = ({ icon: Icon, label, value, color = "slate", delay = 0 }) => {
  const colorClasses = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: EASE }}
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${colorClasses[color]} transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default`}
    >
      <div className="p-2 rounded-xl bg-white/80 shadow-sm">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xs font-medium opacity-70">{label}</p>
        <p className="text-lg font-bold -mt-0.5">{value}</p>
      </div>
    </motion.div>
  );
};

// Normalise leave type name to a key for colour lookups
const normaliseLeaveType = (leaveType) => {
  if (!leaveType) return 'other';
  return leaveType.toLowerCase().replace(/\s+/g, '_');
};

// Leave type color mapping for accent bars
const LEAVE_ACCENT_MAP = {
  annual_leave: 'bg-emerald-500',
  sick_leave: 'bg-rose-500',
  personal_leave: 'bg-violet-500',
  work_from_home: 'bg-sky-500',
  training: 'bg-amber-500',
  maternity_leave: 'bg-pink-500',
  paternity_leave: 'bg-indigo-500',
  parental_leave: 'bg-indigo-500',
  unpaid_leave: 'bg-slate-500',
  compassionate_leave: 'bg-purple-500',
  bereavement_leave: 'bg-purple-500',
  birthday_leave: 'bg-pink-500',
  birthday: 'bg-pink-500',
  christmas_shutdown: 'bg-amber-500',
  christmas_shut_down: 'bg-amber-500',
  study_leave: 'bg-sky-500',
  unavailable_to_work: 'bg-slate-500',
};

const getLeaveTypeAccent = (leaveType) => {
  const key = normaliseLeaveType(leaveType);
  return LEAVE_ACCENT_MAP[key] || 'bg-slate-400';
};

// Check if a leave type is sick leave
const isSickLeaveType = (leaveType) => normaliseLeaveType(leaveType) === 'sick_leave';

// Card background colors for sick leave (admin only) - calendar cells
const getLeaveCardClasses = (leaveType, isAdmin) => {
  if (isAdmin && isSickLeaveType(leaveType)) {
    return 'bg-rose-50 border-rose-200/60 text-rose-800';
  }
  return 'bg-white/80 border-slate-200/60 text-slate-700';
};

// Tooltip detail card backgrounds for sick leave (admin only)
const getLeaveTooltipClasses = (leaveType, isAdmin) => {
  if (isAdmin && isSickLeaveType(leaveType)) {
    return 'bg-rose-50/50 border-rose-200/50';
  }
  return 'bg-slate-50/50 border-slate-200/50';
};


// Leave card for Today's Summary
const LeaveCard = ({ request, getDayTypeDisplay, getLeaveTypeName, getLeaveTypeColor, delay = 0 }) => {
  const today = new Date();
  const dayTypeDisplay = getDayTypeDisplay(request, today);
  const accentColor = getLeaveTypeAccent(request.leave_type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: EASE }}
      className="group relative bg-white rounded-xl border border-slate-200/80 p-4 pl-5 transition-all duration-300 hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:border-slate-300/80"
    >
      <div className={`absolute left-0 top-3 bottom-3 w-1 ${accentColor} rounded-full`} />

      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-semibold text-slate-800">
            {request.user_profiles?.first_name} {request.user_profiles?.last_name}
          </p>
          <p className="text-sm text-slate-500 mt-0.5">
            {getLeaveTypeName(request.leave_type, request.user_id)}
            {dayTypeDisplay !== 'Full Day' && (
              <span className="ml-1 text-slate-400">({dayTypeDisplay})</span>
            )}
          </p>
        </div>
        <div className={cn(
          "px-2.5 py-1 rounded-full text-xs font-medium",
          getLeaveTypeColor(request.leave_type, request.user_id)
        )}>
          {request.leave_type?.charAt(0).toUpperCase() + request.leave_type?.slice(1)}
        </div>
      </div>
    </motion.div>
  );
};

export const MyCalendarPage = () => {
  const { userProfile, company, can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  usePageTitle('Team Calendar');

  // Initialize currentDate from URL params or default to today
  const getInitialDate = () => {
    const yearParam = searchParams.get('year');
    const monthParam = searchParams.get('month');

    if (yearParam && monthParam) {
      const year = parseInt(yearParam);
      const month = parseInt(monthParam) - 1;

      if (year >= 1900 && year <= 2100 && month >= 0 && month <= 11) {
        return new Date(year, month, 1);
      }
    }

    return new Date();
  };

  const [currentDate, setCurrentDate] = useState(getInitialDate);

  // Dialog states
  const [contextMenu, setContextMenu] = useState(null);
  const [noteDialog, setNoteDialog] = useState({ open: false, date: null, note: null });
  const [deleteNoteDialog, setDeleteNoteDialog] = useState({ open: false, note: null });
  const [noteText, setNoteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [dateDetailDialog, setDateDetailDialog] = useState({ open: false, date: null });

  const canManageAttendance = can('attendance.manage');

  const {
    loading,
    todayRequests,
    daysInMonth,
    refetch,
    getRequestsForDate,
    isBankHoliday,
    getBankHolidayName,
    getNoteForDate,
    getLeaveTypeColor,
    getLeaveTypeName,
    getLeaveTypeAbbr,
    getDayTypeDisplay,
    getDayTypeAbbr,
    formatDateRange,
    isToday,
    isWeekend,
    months,
    years
  } = useTeamCalendar(company?.id, currentDate, canManageAttendance, userProfile?.id);

  // Memoized today's bank holiday
  const todayBankHoliday = useMemo(() => {
    const today = new Date();
    if (isBankHoliday(today)) {
      return { name: getBankHolidayName(today) };
    }
    return null;
  }, [isBankHoliday, getBankHolidayName]);

  // Count unique people on leave this month (excluding sick leave)
  const monthlyLeaveCount = useMemo(() => {
    const uniqueUsers = new Set();
    daysInMonth.forEach(date => {
      if (date) {
        const requests = getRequestsForDate(date);
        requests.filter(req => !isSickLeaveType(req.leave_type)).forEach(req => uniqueUsers.add(req.user_id));
      }
    });
    return uniqueUsers.size;
  }, [daysInMonth, getRequestsForDate]);

  // Count bank holidays this month
  const bankHolidaysThisMonth = useMemo(() => {
    let count = 0;
    daysInMonth.forEach(date => {
      if (date && isBankHoliday(date)) {
        count++;
      }
    });
    return count;
  }, [daysInMonth, isBankHoliday]);

  // Count total leave requests this month (excluding sick leave)
  const totalLeaveRequests = useMemo(() => {
    let count = 0;
    daysInMonth.forEach(date => {
      if (date) {
        count += getRequestsForDate(date).filter(req => !isSickLeaveType(req.leave_type)).length;
      }
    });
    return count;
  }, [daysInMonth, getRequestsForDate]);

  // Count sick leave days this month (admin only)
  const sickLeaveDays = useMemo(() => {
    if (!canManageAttendance) return 0;
    let count = 0;
    daysInMonth.forEach(date => {
      if (date) {
        const requests = getRequestsForDate(date);
        count += requests.filter(req => isSickLeaveType(req.leave_type)).length;
      }
    });
    return count;
  }, [daysInMonth, getRequestsForDate, canManageAttendance]);

  // Helper to update both state and URL params
  const updateDate = useCallback((newDate) => {
    setCurrentDate(newDate);
    const year = newDate.getFullYear();
    const month = newDate.getMonth() + 1;
    setSearchParams(
      { year: year.toString(), month: month.toString() },
      { replace: true }
    );
  }, [setSearchParams]);

  const previousMonth = useCallback(() => {
    updateDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  }, [currentDate, updateDate]);

  const nextMonth = useCallback(() => {
    updateDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  }, [currentDate, updateDate]);

  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentDate(today);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleMonthChange = useCallback((monthIndex) => {
    updateDate(new Date(currentDate.getFullYear(), parseInt(monthIndex)));
  }, [currentDate, updateDate]);

  const handleYearChange = useCallback((year) => {
    updateDate(new Date(parseInt(year), currentDate.getMonth()));
  }, [currentDate, updateDate]);

  const handleDateClick = useCallback((date) => {
    setDateDetailDialog({ open: true, date });
  }, []);

  const closeDateDetailDialog = useCallback(() => {
    setDateDetailDialog({ open: false, date: null });
  }, []);

  const handleContextMenu = useCallback((e, date) => {
    if (!canManageAttendance) return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, date });
  }, [canManageAttendance]);

  const openNoteDialog = useCallback((date, existingNote = null) => {
    setNoteDialog({ open: true, date, note: existingNote });
    setNoteText(existingNote?.note_text || '');
    setContextMenu(null);
  }, []);

  const closeNoteDialog = useCallback(() => {
    setNoteDialog({ open: false, date: null, note: null });
    setNoteText('');
  }, []);

  const handleSaveNote = useCallback(async () => {
    if (!noteText.trim()) {
      toast.error('Note text cannot be empty');
      return;
    }

    try {
      const dateStr = noteDialog.date.toISOString().split('T')[0];

      if (noteDialog.note) {
        const { error } = await supabase
          .from('attendance_calendar_notes')
          .update({ note_text: noteText })
          .eq('id', noteDialog.note.id);

        if (error) throw error;
        toast.success('Note updated');
      } else {
        const { error } = await supabase
          .from('attendance_calendar_notes')
          .insert({
            company_id: company.id,
            note_date: dateStr,
            note_text: noteText,
            created_by: userProfile.id
          });

        if (error) {
          if (error.code === '23505') {
            toast.error('A note already exists for this date');
          } else {
            throw error;
          }
          return;
        }
        toast.success('Note added');
      }

      closeNoteDialog();
      refetch();
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error('Failed to save note');
    }
  }, [noteText, noteDialog, company?.id, userProfile?.id, closeNoteDialog, refetch]);

  const openDeleteNoteDialog = useCallback((note) => {
    setDeleteNoteDialog({ open: true, note });
  }, []);

  const closeDeleteNoteDialog = useCallback(() => {
    setDeleteNoteDialog({ open: false, note: null });
  }, []);

  const handleDeleteNote = useCallback(async () => {
    if (!deleteNoteDialog.note) return;

    try {
      setDeleting(true);
      const { error } = await supabase
        .from('attendance_calendar_notes')
        .delete()
        .eq('id', deleteNoteDialog.note.id);

      if (error) throw error;

      toast.success('Note deleted');
      closeDeleteNoteDialog();
      closeNoteDialog();
      refetch();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    } finally {
      setDeleting(false);
    }
  }, [deleteNoteDialog.note, closeDeleteNoteDialog, closeNoteDialog, refetch]);

  const handleDocumentClick = useCallback(() => setContextMenu(null), []);

  // Loading state
  if (loading && !daysInMonth.length) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
          <motion.div
            className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
        <p className="text-sm text-slate-500 font-medium">Loading calendar...</p>
      </div>
    );
  }

  const currentMonthName = currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6" onClick={handleDocumentClick}>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Team Calendar</h1>
            <p className="text-slate-500 mt-1">View team availability and schedule at a glance</p>
          </div>
          <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
            <Calendar className="h-6 w-6 text-slate-600" />
          </div>
        </div>

        {/* Stats Row */}
        <div className={cn("grid grid-cols-2 gap-3", canManageAttendance ? "md:grid-cols-5" : "md:grid-cols-4")}>
          <StatPill
            icon={Users}
            label="On Leave Today"
            value={todayRequests.length}
            color={todayRequests.length > 0 ? "amber" : "emerald"}
            delay={0.1}
          />
          <StatPill
            icon={Users}
            label="On Leave This Month"
            value={monthlyLeaveCount}
            color={monthlyLeaveCount > 0 ? "sky" : "emerald"}
            delay={0.15}
          />
          <StatPill
            icon={Sun}
            label="Bank Holidays"
            value={bankHolidaysThisMonth}
            color={bankHolidaysThisMonth > 0 ? "amber" : "slate"}
            delay={0.2}
          />
          <StatPill
            icon={CalendarDays}
            label="Leave Days"
            value={totalLeaveRequests}
            color="violet"
            delay={0.25}
          />
          {canManageAttendance && (
            <StatPill
              icon={Thermometer}
              label="Sick Days"
              value={sickLeaveDays}
              color="rose"
              delay={0.3}
            />
          )}
        </div>
      </motion.div>

      {/* Calendar Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        {/* Calendar Controls */}
        <div className="flex items-center justify-between mb-6 pb-6 border-b border-slate-100">
          <button
            onClick={previousMonth}
            className="p-2.5 rounded-full bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-all duration-200"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-3">
            <Select
              value={currentDate.getMonth().toString()}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="w-[140px] rounded-full border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={currentDate.getFullYear().toString()}
              onValueChange={handleYearChange}
            >
              <SelectTrigger className="w-[100px] rounded-full border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year.value} value={year.value}>
                    {year.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="h-6 w-px bg-slate-200" />

            <button
              onClick={goToToday}
              className="px-4 py-2 rounded-full bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 transition-colors flex items-center gap-2"
            >
              <Calendar className="h-3.5 w-3.5" />
              Today
            </button>
          </div>

          <button
            onClick={nextMonth}
            className="p-2.5 rounded-full bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-all duration-200"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="relative">
              <div className="w-12 h-12 rounded-full border-4 border-slate-100" />
              <motion.div
                className="absolute inset-0 w-12 h-12 rounded-full border-4 border-transparent border-t-slate-500"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div className="grid grid-cols-7 gap-1">
              {daysInMonth.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="h-24" />;
                }

                const leaveRequests = getRequestsForDate(date);
                const note = getNoteForDate(date);
                const isBH = isBankHoliday(date);
                const holidayName = getBankHolidayName(date);
                const weekend = isWeekend(date);
                const today = isToday(date);
                const hasEvents = isBH || leaveRequests.length > 0 || note;

                const MAX_DISPLAY = 2;
                const displayedRequests = leaveRequests.slice(0, MAX_DISPLAY);
                const remainingCount = leaveRequests.length - MAX_DISPLAY;
                const calendarCell = (
                  <div
                    onClick={() => handleDateClick(date)}
                    onContextMenu={(e) => handleContextMenu(e, date)}
                    className={cn(
                      "h-24 rounded-xl p-2 transition-all duration-200 cursor-pointer relative border",
                      today && "ring-2 ring-slate-800 bg-slate-50 border-transparent",
                      !today && weekend && "bg-slate-50/50 border-slate-100",
                      !today && !weekend && "bg-white border-slate-100 hover:border-slate-200 hover:shadow-sm",
                      isBH && !today && "bg-amber-50/50 border-amber-200/50",
                      leaveRequests.length > 0 && !today && !isBH && "bg-sky-50/30 border-sky-200/50",
                      note && canManageAttendance && !today && "border-violet-300"
                    )}
                  >
                    {/* Bank Holiday indicator */}
                    {isBH && (
                      <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold">
                        BH
                      </div>
                    )}

                    <div className="flex flex-col h-full">
                      {/* Date Number and Note Icon */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className={cn(
                          "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold transition-colors",
                          today && "bg-slate-800 text-white",
                          weekend && !today && "text-slate-400",
                          !weekend && !today && "text-slate-700"
                        )}>
                          {date.getDate()}
                        </div>
                        {note && canManageAttendance && (
                          <StickyNote className={cn("h-3.5 w-3.5 text-violet-500", isBH && "mr-7")} />
                        )}
                      </div>

                      {/* Leave Requests */}
                      <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                        {displayedRequests.map((request, idx) => {
                          const dayTypeAbbr = getDayTypeAbbr(request, date);
                          const accentColor = getLeaveTypeAccent(request.leave_type);
                          return (
                            <div
                              key={`${request.id}-${idx}`}
                              className={`relative text-[10px] pl-2 pr-1.5 py-0.5 border rounded truncate font-medium flex items-center justify-between gap-1 shadow-sm ${getLeaveCardClasses(request.leave_type, canManageAttendance)}`}
                              title={`${request.user_profiles?.first_name} ${request.user_profiles?.last_name} - ${getLeaveTypeName(request.leave_type, request.user_id)}`}
                            >
                              <div className={`absolute left-0 top-1 bottom-1 w-0.5 ${accentColor} rounded-full`} />
                              <span className="truncate text-slate-700">
                                {request.user_profiles?.first_name} {request.user_profiles?.last_name?.[0]}.
                              </span>
                              {dayTypeAbbr && (
                                <span className="text-[8px] font-bold text-slate-500 flex-shrink-0">
                                  {dayTypeAbbr}
                                </span>
                              )}
                            </div>
                          );
                        })}
                        {remainingCount > 0 && (
                          <div className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-medium text-center">
                            +{remainingCount} more
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );

                if (!hasEvents) {
                  return <div key={date.toISOString()}>{calendarCell}</div>;
                }

                return (
                  <Tooltip key={date.toISOString()}>
                    <TooltipTrigger asChild>
                      {calendarCell}
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs p-0 max-h-[400px] overflow-y-auto bg-white border-slate-200 shadow-xl rounded-xl">
                      <div className="space-y-2 p-4">
                        {/* Date header */}
                        <div className="font-semibold text-sm text-slate-800 border-b border-slate-100 pb-2 sticky top-0 bg-white z-10">
                          {date.toLocaleDateString('en-GB', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>

                        {/* Admin Note */}
                        {note && canManageAttendance && (
                          <div className="relative p-3 pl-4 bg-violet-50/50 rounded-lg border border-violet-200/50">
                            <div className="absolute left-0 top-2 bottom-2 w-1 bg-violet-500 rounded-full" />
                            <div className="flex items-start gap-2">
                              <StickyNote className="h-4 w-4 text-violet-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-violet-900">Admin Note</div>
                                <div className="text-xs text-violet-700 whitespace-pre-wrap mt-1">{note.note_text}</div>
                                <div className="flex gap-2 mt-2">
                                  <button
                                    className="text-xs text-slate-600 hover:text-slate-800 font-medium flex items-center gap-1"
                                    onClick={() => openNoteDialog(date, note)}
                                  >
                                    <Edit2 className="h-3 w-3" />
                                    Edit
                                  </button>
                                  <button
                                    className="text-xs text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1"
                                    onClick={() => openDeleteNoteDialog(note)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Bank Holiday */}
                        {isBH && (
                          <div className="relative p-3 pl-4 bg-amber-50/50 rounded-lg border border-amber-200/50">
                            <div className="absolute left-0 top-2 bottom-2 w-1 bg-amber-500 rounded-full" />
                            <div className="flex items-start gap-2">
                              <Calendar className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="font-medium text-sm text-amber-900">Bank Holiday</div>
                                <div className="text-xs text-amber-700">{holidayName}</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Leave Requests */}
                        {leaveRequests.map((request, idx) => {
                          const dayTypeDisplay = getDayTypeDisplay(request, date);
                          const accentColor = getLeaveTypeAccent(request.leave_type);
                          return (
                            <div key={`${request.id}-${idx}`} className={`relative p-3 pl-4 rounded-lg border ${getLeaveTooltipClasses(request.leave_type, canManageAttendance)}`}>
                              <div className={`absolute left-0 top-2 bottom-2 w-1 ${accentColor} rounded-full`} />
                              <div className="flex items-start gap-2">
                                <div className={cn("px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0", getLeaveTypeColor(request.leave_type, request.user_id))}>
                                  {getLeaveTypeAbbr(request.leave_type, request.user_id)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm text-slate-800">
                                    {getLeaveTypeName(request.leave_type, request.user_id)}
                                    <span className="font-normal text-slate-500 ml-1">({dayTypeDisplay})</span>
                                  </div>
                                  <div className="text-xs text-slate-700 font-medium mt-0.5">
                                    {request.user_profiles?.first_name} {request.user_profiles?.last_name}
                                  </div>
                                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                                    <Clock className="h-3 w-3" />
                                    {formatDateRange(request.start_date, request.end_date)}
                                  </div>
                                  {request.user_note && (request.user_id === userProfile?.id || canManageAttendance) && (
                                    <div className="flex items-start gap-1 text-xs text-slate-500 mt-1">
                                      <FileText className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                      <span className="line-clamp-2">{request.user_note}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </motion.div>

      {/* Today's Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Today's Summary</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {new Date().toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
          {todayRequests.length > 0 && (
            <div className="px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold">
              {todayRequests.length} {todayRequests.length === 1 ? 'person' : 'people'} away
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full border-4 border-slate-100" />
              <motion.div
                className="absolute inset-0 w-10 h-10 rounded-full border-4 border-transparent border-t-slate-500"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Bank Holiday Alert */}
            {todayBankHoliday && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="relative p-4 pl-5 bg-amber-50/50 rounded-xl border border-amber-200/50"
              >
                <div className="absolute left-0 top-3 bottom-3 w-1 bg-amber-500 rounded-full" />
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-amber-100">
                    <Sun className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-900">Bank Holiday</p>
                    <p className="text-sm text-amber-700">{todayBankHoliday.name}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* People on Leave Today */}
            {todayRequests.length > 0 ? (
              <div className="space-y-3">
                {todayRequests.map((request, idx) => (
                  <LeaveCard
                    key={request.id}
                    request={request}
                    getDayTypeDisplay={getDayTypeDisplay}
                    getLeaveTypeName={getLeaveTypeName}
                    getLeaveTypeColor={getLeaveTypeColor}
                    delay={0.05 * idx}
                  />
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="text-center py-12"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 mb-4">
                  <Users className="h-6 w-6 text-emerald-600" />
                </div>
                <p className="text-[15px] font-semibold text-slate-700">Everyone is available today</p>
                <p className="text-sm text-slate-400 mt-1">No team members on leave</p>
              </motion.div>
            )}
          </div>
        )}
      </motion.div>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed bg-white border border-slate-200 shadow-xl rounded-xl py-2 z-50 min-w-[160px]"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center gap-3 text-slate-700 font-medium transition-colors"
              onClick={() => openNoteDialog(contextMenu.date)}
            >
              <Plus className="h-4 w-4 text-slate-500" />
              Add Note
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={closeNoteDialog}>
        <DialogContent className="rounded-2xl border-slate-200">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="text-xl font-bold text-slate-800">
              {noteDialog.note ? 'Edit' : 'Add'} Calendar Note
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {noteDialog.date && noteDialog.date.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note-text" className="text-sm font-medium text-slate-700">Note</Label>
              <Textarea
                id="note-text"
                placeholder="Enter your note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
                className="rounded-xl border-slate-200 focus:border-slate-300 focus:ring-slate-200 resize-none"
              />
            </div>
          </div>

          <DialogFooter className="pt-4 border-t border-slate-100 gap-3">
            <Button variant="outline" onClick={closeNoteDialog} className="rounded-full border-slate-200">
              Cancel
            </Button>
            <Button onClick={handleSaveNote} className="rounded-full bg-slate-800 hover:bg-slate-700">
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Detail Dialog */}
      <Dialog open={dateDetailDialog.open} onOpenChange={closeDateDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl border-slate-200">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="text-xl font-bold text-slate-800">
              {dateDetailDialog.date && dateDetailDialog.date.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {dateDetailDialog.date && (() => {
              const date = dateDetailDialog.date;
              const leaveRequests = getRequestsForDate(date);
              const note = getNoteForDate(date);
              const isBH = isBankHoliday(date);
              const holidayName = getBankHolidayName(date);

              return (
                <>
                  {/* Bank Holiday */}
                  {isBH && (
                    <div className="relative p-4 pl-5 bg-amber-50/50 rounded-xl border border-amber-200/50">
                      <div className="absolute left-0 top-3 bottom-3 w-1 bg-amber-500 rounded-full" />
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-xl bg-amber-100">
                          <Calendar className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-amber-900">Bank Holiday</p>
                          <p className="text-sm text-amber-700">{holidayName}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Admin Note */}
                  {note && canManageAttendance && (
                    <div className="relative p-4 pl-5 bg-violet-50/50 rounded-xl border border-violet-200/50">
                      <div className="absolute left-0 top-3 bottom-3 w-1 bg-violet-500 rounded-full" />
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-xl bg-violet-100">
                          <StickyNote className="h-5 w-5 text-violet-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-violet-900 mb-1">Admin Note</p>
                          <p className="text-sm text-violet-700 whitespace-pre-wrap">{note.note_text}</p>
                          {canManageAttendance && (
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full border-slate-200 text-sm"
                                onClick={() => {
                                  closeDateDetailDialog();
                                  openNoteDialog(date, note);
                                }}
                              >
                                <Edit2 className="h-3 w-3 mr-1.5" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50 text-sm"
                                onClick={() => openDeleteNoteDialog(note)}
                              >
                                <Trash2 className="h-3 w-3 mr-1.5" />
                                Delete
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Leave Requests */}
                  {leaveRequests.length > 0 ? (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-sm text-slate-600 uppercase tracking-wider">
                        Leave Requests ({leaveRequests.length})
                      </h3>
                      {leaveRequests.map((request) => {
                        const dayTypeDisplay = getDayTypeDisplay(request, date);
                        const accentColor = getLeaveTypeAccent(request.leave_type);
                        return (
                          <div
                            key={request.id}
                            className="relative p-4 pl-5 bg-white rounded-xl border border-slate-200"
                          >
                            <div className={`absolute left-0 top-3 bottom-3 w-1 ${accentColor} rounded-full`} />
                            <div className="flex items-start gap-3">
                              <div className={cn("px-2 py-1 rounded-lg text-xs font-semibold flex-shrink-0", getLeaveTypeColor(request.leave_type, request.user_id))}>
                                {getLeaveTypeAbbr(request.leave_type, request.user_id)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-800">
                                  {request.user_profiles?.first_name} {request.user_profiles?.last_name}
                                </p>
                                <p className="text-sm text-slate-600 mt-0.5">
                                  {getLeaveTypeName(request.leave_type, request.user_id)}
                                  <span className="text-slate-400 ml-1">({dayTypeDisplay})</span>
                                </p>
                                <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                                  <Clock className="h-3 w-3" />
                                  {formatDateRange(request.start_date, request.end_date)}
                                </div>
                                {request.user_note && (request.user_id === userProfile?.id || canManageAttendance) && (
                                  <div className="mt-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                                    <div className="flex items-start gap-2 text-sm text-slate-600">
                                      <FileText className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-400" />
                                      <span className="whitespace-pre-wrap">{request.user_note}</span>
                                    </div>
                                  </div>
                                )}
                                <div className="mt-3">
                                  <span className={cn(
                                    "px-2.5 py-1 rounded-full text-xs font-semibold",
                                    request.status === 'approved' && "bg-emerald-50 text-emerald-700 border border-emerald-200",
                                    request.status === 'pending' && "bg-amber-50 text-amber-700 border border-amber-200",
                                    request.status === 'rejected' && "bg-rose-50 text-rose-700 border border-rose-200"
                                  )}>
                                    {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    !isBH && (!note || !canManageAttendance) && (
                      <div className="text-center py-12">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 mb-4">
                          <Calendar className="h-6 w-6 text-slate-400" />
                        </div>
                        <p className="text-[15px] font-medium text-slate-500">No leave requests for this day</p>
                      </div>
                    )
                  )}

                  {/* Add Note Button for Admins */}
                  {canManageAttendance && !note && (
                    <Button
                      variant="outline"
                      className="w-full rounded-full border-slate-200 text-slate-700"
                      onClick={() => {
                        closeDateDetailDialog();
                        openNoteDialog(date);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Admin Note
                    </Button>
                  )}
                </>
              );
            })()}
          </div>

          <DialogFooter className="pt-4 border-t border-slate-100">
            <Button variant="outline" onClick={closeDateDetailDialog} className="rounded-full border-slate-200">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Note Confirmation Dialog */}
      <Dialog open={deleteNoteDialog.open} onOpenChange={closeDeleteNoteDialog}>
        <DialogContent className="sm:max-w-[425px] rounded-2xl border-slate-200">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="text-xl font-bold text-slate-800">Delete Calendar Note</DialogTitle>
            <DialogDescription className="text-slate-500">
              Are you sure you want to delete this note? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteNoteDialog.note && (
            <div className="py-4">
              <div className="p-4 rounded-xl bg-rose-50/50 border border-rose-200/50">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {deleteNoteDialog.note.note_text}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="pt-4 border-t border-slate-100 gap-3">
            <Button variant="outline" onClick={closeDeleteNoteDialog} disabled={deleting} className="rounded-full border-slate-200">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteNote}
              disabled={deleting}
              className="rounded-full bg-rose-600 hover:bg-rose-700"
            >
              {deleting ? (
                <>
                  <motion.div
                    className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white mr-2"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Note
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
